import { CloseOutlined } from '@ant-design/icons';
import { useEffect } from 'react';
import styled from 'styled-components';

import { Flex, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import IconButton from '@/components/Button/IconButton';
import Transition from '@/components/Common/Transition';
import Input from '@/components/Inputs/Input';
import { useModalContext } from '@/components/Modal/Modal';
import Heading from '@/components/Typography/Heading';
import Typo from '@/components/Typography/Typo';
import ActionBox from '@/features/Workflow/WorkflowDetail/subscription2/ActionBox';
import Subscription2ContextProvider, {
  useSubscription2Context,
} from '@/features/Workflow/WorkflowDetail/subscription2/SubscriptionContextProvider';
import TriggerBox from '@/features/Workflow/WorkflowDetail/subscription2/TriggerBox';
import useWorkflowDetail from '@/features/Workflow/WorkflowDetail/useNotiDetail';
import { useMessage } from '@/messages';
import { modalStyle } from '@/styles/style';

type ModalProps = {
  dashboardAlias: string;
  dashboardName: string;
};

const CreateSubscribeModal = (props: ModalProps) => {
  const { closeModal } = useModalContext();

  return (
    <Style>
      <Subscription2ContextProvider mode="CREATE">
        <DetailContent
          dashboardAlias={props.dashboardAlias}
          dashboardName={props.dashboardName}
          closeModal={closeModal}
        />
      </Subscription2ContextProvider>
    </Style>
  );
};

const Style = styled.div`
  width: 700px;
  ${modalStyle};
`;

type ContentProps = {
  dashboardAlias: string;
  dashboardName: string;
  closeModal: () => void;
};

const DetailContent = (props: ContentProps) => {
  const context = useSubscription2Context();
  const m = useMessage();
  const { onPublish, onTest } = useWorkflowDetail({
    mode: context.mode,
    alias: context.alias,
    initChange() {
      context.setHasChange(false);
    },
  });

  const clickHandler = async (type: 'SAVE' | 'TEST') => {
    if (!context) return;
    const { target } = context.triggerState.state;
    // validation에서 target이 설정되었는지 확인되므로 타입 단언
    if (!target) return;

    const params: SubscriptionWorkflowState = {
      ...context.triggerState.state,
      target,
    };

    if (type === 'SAVE') {
      onPublish(
        {
          ...context.actionState.state,
          subscription: params,
        },
        () => {
          props.closeModal();
        },
      );
    }

    if (type === 'TEST') {
      onTest({
        ...context.actionState.state,
        subscription: params,
      });
    }
  };

  useEffect(() => {
    context.actionState.setValue('title', props.dashboardName);
  }, [props.dashboardName]);

  return (
    <VFlex g={32} padding="32px">
      <VFlex g={8}>
        <Flex j="space-between" a="center">
          <Heading type="h3">Subscribe dashboard</Heading>
          <IconButton
            sizeType="m"
            outlined={false}
            icon={<CloseOutlined />}
            onClick={props.closeModal}
          />
        </Flex>

        <Typo type="body2">{m.workflow('WORKFLOW_DESCRIPTION')}</Typo>
      </VFlex>
      <VFlex g={16}>
        <Input
          sizeType="xl"
          value={context.actionState.state.title}
          onChange={(e) => {
            context.actionState.setValue('title', e.target.value);
          }}
          placeholder="Type notification title..."
        />

        <Transition status={true}>
          <TriggerBox dashboardAlias={props.dashboardAlias} />
        </Transition>

        <Transition
          status={context.triggerValidated.validated}
          name="action-box"
        >
          <ActionBox />
        </Transition>

        <Transition
          status={
            context.triggerValidated.validated &&
            context.actionValidated.validated
          }
        >
          <Flex g={8} j="flex-end" style={{ paddingTop: 16 }}>
            <Button
              sizeType="l"
              variant="outlined"
              style={{ width: 100 }}
              onClick={() => clickHandler('TEST')}
            >
              Test
            </Button>
            <Button
              sizeType="l"
              variant="primary"
              style={{ width: 100 }}
              onClick={() => clickHandler('SAVE')}
              disabled={!context.hasChange}
            >
              Subscribe
            </Button>
          </Flex>
        </Transition>
      </VFlex>
    </VFlex>
  );
};

export default CreateSubscribeModal;
